import {
    Button,
    Card,
    makeStyles,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Text,
    Title3,
    tokens
} from "@fluentui/react-components";
import {ArrowSync24Regular} from "@fluentui/react-icons";
import React from "react";
import {useApiQuery} from "../../services/useApiQuery.ts";
import {Module} from "../../services/types.ts";
import {useTranslation} from "react-i18next";

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '16px',
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        marginBottom: '16px',
        '@media (min-width: 641px)': {
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
    },
    actionRow: {
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        '@media (min-width: 641px)': {
            width: 'auto',
            justifyContent: 'flex-end',
        },
    },
    backButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
    },
    actionArea: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: '16px',
        marginTop: '16px',
        padding: '16px',
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: '4px',
    },
    formField: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        minWidth: '250px',
    },
    startButton: {
        marginLeft: 'auto',
    },
    errorContainer: {
        padding: '16px',
        backgroundColor: tokens.colorPaletteRedBackground1,
        borderRadius: '4px',
        marginBottom: '16px',
    },
    successContainer: {
        padding: '16px',
        backgroundColor: tokens.colorPaletteGreenBackground1,
        borderRadius: '4px',
        marginBottom: '16px',
    },
    responsiveTable: {
        '@media (max-width: 640px)': {
            '& table': {
                display: 'block',
                width: '100%',
            },
            '& thead': {
                display: 'none',
            },
            '& tbody': {
                display: 'block',
                width: '100%',
            },
            '& tr': {
                display: 'flex',
                flexDirection: 'column',
                padding: '12px 0',
                marginBottom: '8px',
                '&:not(:last-child)': {
                    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
                    paddingBottom: '16px',
                },
            },
            '& td': {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                border: 'none',
                padding: '8px 0',
                width: '100%',
                position: 'relative',
                '&::before': {
                    content: 'attr(data-label)',
                    fontWeight: 'bold',
                    marginRight: '24px',
                    minWidth: '80px',
                    flexShrink: 0,
                },
            },
            '& [data-label="Description"]': {
                alignItems: 'flex-start',
                wordBreak: 'break-word',
                '& > *': {
                    maxWidth: '60%',
                    overflowWrap: 'break-word',
                    wordWrap: 'break-word',
                    hyphens: 'auto',
                },
            },
            '& [data-label="Actions"]': {
                paddingTop: '8px',
                marginTop: '8px',
            },
            '& [role="cell-actions"]': {
                opacity: '1 !important',
                // @ts-ignore
                position: 'static !important',
                transform: 'none !important',
                padding: '0 !important',
            },
        },
    },
});

export const SeriesTab: React.FC = () => {
    const styles = useStyles();
    const { t } = useTranslation();

    const {
        data: modules,
        isPending: modulesLoading,
        isRefetching: isRefetchingModules,
        refetch: refetchModules,
    } = useApiQuery<Module[]>('/api/module/list');

    return <Card>
        <div className={styles.container}>
            <div className={styles.header}>
                <Title3>{t("nav.message_series")}</Title3>
                <div className={styles.actionRow}>
                    <Button
                        appearance="subtle"
                        icon={(isRefetchingModules || modulesLoading) ?
                            <Spinner size="extra-tiny"/> : <ArrowSync24Regular/>}
                        onClick={() => {
                            refetchModules();
                        }}
                        disabled={isRefetchingModules || modulesLoading}
                    >
                        {t("button.refresh")}
                    </Button>
                </div>
            </div>

            {modulesLoading ? (
                <Spinner label={t("text.loading-message-series")}/>
            ) : modules?.length === 0 ? (
                <Text>No message series found.</Text>
            ) : (
                <div className={styles.responsiveTable}>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>{t("table.header.name")}</TableHeaderCell>
                                <TableHeaderCell>{t("table.header.name")}</TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {modules?.map((module) => (
                                <TableRow key={module.id}>
                                    <TableCell data-label={t("table.header.name")}>{module.name}</TableCell>
                                    <TableCell data-label={t("table.header.description")}>{module.description}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
        </div>
    </Card>
};